import type { SxProps, Theme } from "@mui/material";
import { gridClasses } from "@mui/x-data-grid";
import type { GridApiPro, GridColDef, GridRowParams } from "@mui/x-data-grid-pro";
import type { ExtendedCol } from "src/business-components/tables/columns.types";
import type { MetricsTableRow } from "src/pages/PipelinePage/MetricsTabView/MetricsTable/MetricsTable.interface";
import type { MetricConf } from "src/queries/metrics/metricsConf.types";
import { getMetricUniqueKey } from "@utils/getMetricUniqueKey";
import { METRIC_GROUP_TYPE_ALL } from "../components/MetricsTableMetricsFilterButtonGroup/MetricsTableMetricsFilterButtonGroup.constants";
import {
  ASSET_PATH_DELIMITER,
  COMPOUND_ROW_ID_PREFIX,
} from "../MetricsTabView.constants";
import type { HeaderFilterKey } from "./MetricsTable.constants";
import {
  CLASS_NAMES,
  HEADER_FILTER_LABELS,
  TEST_FILTER_LABELS,
} from "./MetricsTable.constants";
import type { TestFilterOptions } from "./MetricsTable.types";

export type ActiveFilterChip = {
  /** Stable react key + test id suffix */
  id: string;
  /** Which filter source it came from — the hook uses it to undo the filter */
  kind: "search" | "header" | "tests" | "metricGroup" | "asset";
  /** Search term / header filter field / asset filter key */
  key: string;
  label: string;
  value: string;
};

type ActiveFilterState = {
  quickFilter: string[];
  headerFilters: { [k: string]: string };
  testsFilter: TestFilterOptions;
  metricGroupType: string | null;
  assetFilters: { [k in "path" | "asset_type"]: string };
};

/**
 * Flattens every active metrics-table filter into removable chips. Pure on purpose:
 * the atoms/search-params live in `useTableFilters`, which maps `kind`+`key` back to
 * the setter that clears the filter.
 */
export const buildActiveFilterChips = ({
  quickFilter,
  headerFilters,
  testsFilter,
  metricGroupType,
  assetFilters,
}: ActiveFilterState): ActiveFilterChip[] => [
  ...quickFilter.map((term) => ({
    id: `search--${term}`,
    kind: "search" as const,
    key: term,
    label: "Search",
    value: term,
  })),
  ...Object.entries(headerFilters)
    .filter(([, value]) => !!value)
    .map(([field, value]) => ({
      id: `header--${field}`,
      kind: "header" as const,
      key: field,
      label: HEADER_FILTER_LABELS[field as HeaderFilterKey] ?? field,
      value: String(value),
    })),
  ...(testsFilter
    ? [
        {
          id: "tests",
          kind: "tests" as const,
          key: "tests",
          label: "Tests & alerts",
          value: TEST_FILTER_LABELS[testsFilter],
        },
      ]
    : []),
  ...(metricGroupType && metricGroupType !== METRIC_GROUP_TYPE_ALL
    ? [
        {
          id: "metricGroup",
          kind: "metricGroup" as const,
          key: "metricGroupType",
          label: "Metric group",
          value: metricGroupType[0].toUpperCase() + metricGroupType.slice(1),
        },
      ]
    : []),
  ...(assetFilters.path
    ? [
        {
          id: "asset--path",
          kind: "asset" as const,
          key: "path",
          label: "Asset",
          value: assetFilters.path.split(ASSET_PATH_DELIMITER).join(" / "),
        },
      ]
    : []),
  ...(assetFilters.asset_type
    ? [
        {
          id: "asset--asset_type",
          kind: "asset" as const,
          key: "asset_type",
          label: "Asset type",
          value: assetFilters.asset_type,
        },
      ]
    : []),
];

export const resetTableColumnsVisibility = () => {
  const COL_VISIBILITY_KEY = "metrics-table-column-visibility";
  const COL_VISIBILITY_RESET_KEY = "did-metrics-table-column-visibility-reset";
  const DID_RESET = window.localStorage.getItem(COL_VISIBILITY_RESET_KEY);

  if (!DID_RESET) {
    window.localStorage.removeItem(COL_VISIBILITY_KEY);
    window.localStorage.setItem(COL_VISIBILITY_RESET_KEY, "true");

    window.sessionStorage.removeItem(COL_VISIBILITY_KEY);
    window.sessionStorage.setItem(COL_VISIBILITY_RESET_KEY, "true");
  }
};

export const getRowKey = (row: MetricsTableRow) =>
  row?.asset_value ? row?.uniqueKey : row?.metric_id;

export const getEnhancedMetrics = (_metricsData: MetricConf[]) => {
  const metricsData = [..._metricsData] as MetricsTableRow[];
  const hasAssetValue = (m: any): m is { asset_value: string } =>
    typeof m.asset_value === "string" && m.asset_value;

  const getTableGroupingKey = (m: MetricsTableRow) =>
    `["${m.table_grouping}","${m.task_name}","${m.asset_name}"]`;

  const getFunctionalKey = (m: MetricsTableRow) => {
    // significantly more performant than JSON.stringify:
    return `["${m.app_id}","${m.metric_id}","${m.asset_type}","${m.asset_name}","${m.task_name}","${m.metric_type}","${m.full_asset_path}"]`;
  };

  const grouppedMetricsListsMap = metricsData.reduce((acc, m) => {
    if (!m.table_grouping) {
      return acc;
    }
    const groupingKey = getTableGroupingKey(m);

    if (acc.has(groupingKey)) {
      acc.get(groupingKey)!.push(m);
    } else {
      acc.set(groupingKey, [m]);
    }

    return acc;
  }, new Map<string, MetricsTableRow[]>());

  const compoundMetricsMap = Array.from(grouppedMetricsListsMap.values()).reduce(
    (acc, metricGroup) => {
      if (metricGroup.length < 2) {
        return acc;
      }
      const compoundMetric = metricGroup
        .sort((a, b) => a.metric_id - b.metric_id)
        .at(0);

      if (!compoundMetric) throw new Error("Compound metric not found");
      const groupingKey = getTableGroupingKey(compoundMetric);

      acc.set(
        groupingKey,
        Object.assign({}, compoundMetric, {
          // compoundMetric is the metric with the smallest metric_id in the group.
          // metric_ids are globally unique, so prefix + that id yields a short, stable,
          // collision-free row id.
          uniqueKey: `${COMPOUND_ROW_ID_PREFIX}${compoundMetric.metric_id}`,
          isCompound: true,
          grouping: [groupingKey],
          metric_value: undefined,
          prev_value: undefined,
          metric_id: undefined,
          groupingKey,
        } as Partial<MetricsTableRow>)
      );

      return acc;
    },
    new Map<string, MetricsTableRow>()
  );

  const rows: MetricsTableRow[] = metricsData.map((m) => {
    // Use to MUI's getTreeDataPath to form groups
    const grouping = [];
    const groupingKey = getTableGroupingKey(m);
    const functionalKey = getFunctionalKey(m);
    const validAssetValue = hasAssetValue(m);

    if (validAssetValue) {
      grouping.push(functionalKey);
      grouping.push(m.asset_value);
    } else if (m.table_grouping && compoundMetricsMap.has(groupingKey)) {
      grouping.push(groupingKey);
      grouping.push(functionalKey);
    } else {
      grouping.push(functionalKey);
    }

    const row: MetricsTableRow = Object.assign({}, m, {
      test_id: m?.tests?.at(0)?.test_id,
      uniqueKey: getMetricUniqueKey(m),
      isInGroup: grouping.length > 1,
      isCompound: m?.metric_type === "cnt_values" && !validAssetValue,
      grouping,
      groupingKey,
    });

    return row;
  });

  return rows.concat(Array.from(compoundMetricsMap.values()));
};

/**
 * One row per metric: skips compound group rows (no metric_id) and the per-value
 * child rows of `cnt_values` metrics, which repeat their parent's metric_id.
 */
export const isMetricRow = (
  row: Partial<Pick<MetricsTableRow, "metric_id" | "asset_value">> | undefined
) => !!row?.metric_id && !row.asset_value;

export const getDefaultHiddenColumns = (columns: ExtendedCol<MetricsTableRow>[]) =>
  Object.fromEntries(
    columns.filter((c) => !!c.__definity?.hidden).map((c) => [c.field, false])
  );

export const getTogglableColumns = (columns: GridColDef[]) =>
  columns
    .map(({ field }) => field)
    .filter(
      (c) =>
        ![
          "is_auto",
          "asset_name",
          "schema_name",
          "tests_count",
          "alerts_count",
          "metric_group_type",
          "should_notify",
          "should_break",
          "has_breaking_tests",
          "human_readable_metric_name",
          "__tree_data_group__",
        ].includes(c)
    );

export const getTableSX = (
  shouldHideSingleSelectedCheckbox: boolean,
  shouldDisableRowClick: boolean
): SxProps<Theme> => ({
  fontSize: 12,
  border: "0",

  [`& .${gridClasses.row}.${CLASS_NAMES.HIDE_CHECKBOX} [data-field='__check__'] span`]:
    {
      visibility: "hidden",
    },
  [`& .${gridClasses.columnHeader}[data-field='__check__'] > div`]: {
    display: shouldHideSingleSelectedCheckbox ? "none" : null,
  },
  "& .Mui-selected .MuiDataGrid-cellCheckbox > span": {
    display: shouldHideSingleSelectedCheckbox ? "none" : null,
  },

  [`& .${gridClasses.cell}`]: {
    cursor: shouldDisableRowClick ? "default" : "pointer",
  },

  [`& .${gridClasses.columnHeaderTitleContainer}`]: { pr: "0.2rem" },
  ".MuiDataGrid-columnHeaders .MuiDataGrid-pinnedColumnHeaders": {
    background: "none !important",
    border: "none",
    boxShadow: "none",
  },
});

export const getRowIncidentsBySeverity = (
  params: GridRowParams,
  api: GridApiPro
) => {
  const row = params.row;

  const iterateChildren = (nodeId: number | string): any => {
    const node = api.getRowNode(nodeId);

    if (!node) return {};

    // If current row has tests, include them
    const isCritical = !!api.getRow(nodeId)?.count_breaking_alerts;
    const warning = !!api.getRow(nodeId)?.count_open_alerts;

    // If it's a leaf node (no children), return its tests
    if (node.type !== "group") {
      return { isCritical, warning };
    }

    // Get all child nodes
    const childNodesIds = api.getRowGroupChildren({ groupId: nodeId });

    // Recursively get tests from all children
    const deepChildren =
      childNodesIds?.flatMap((id: number | string) => iterateChildren(id)) || {};

    // Combine current node tests with child nodes
    return {
      isCritical: isCritical || deepChildren.some((child) => child.isCritical),
      warning: warning || deepChildren.some((child) => child.warning),
    };
  };

  const deepOpenAlerts = iterateChildren(row.uniqueKey);

  return deepOpenAlerts;
};
